<script setup lang="ts">
/**
 * SG — Incorporation (Pre) Studio panel.
 * Live document package (package MVP 이식) + L_INCORPORATIONS autofill.
 */
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { fetchSessionClientNm } from '@/features/startTask/preset_task/sg/composables/fetchSessionClientNm';
import { provideCddFill, bareKey, typeOf, isProgressFilled } from './incorporation-pre/cdd/useCddFill';
import {
  provideSgPreSigners,
  SG_PRE_SIGNER_KEYS,
  buildSgPreSignersFromValues,
} from './incorporation-pre/cdd/sgPreSignersContext';
import SgIncorporationPrePackageDoc from './incorporation-pre/SgIncorporationPrePackageDoc.vue';
import {
  mapSgIncorporationToPreValues,
  type SgIncPreIncludes,
} from '../mappers/mapSgIncorporationToPreValues';
import { normalizeSgPreFormDataForWord } from '../utils/normalizeSgPreFormDataForWord';
import { contactsApi } from '@/features/Contact/api/contactsApi';
import {
  loadCmBaseCodeOptions,
  nationalityLabelForOutput,
} from '@/features/startTask/preset_task/sg/composables/cmBaseCodeOptions';
import './incorporation-pre/allotDoc.css';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: Record<string, any> | null;
  editTaskId?: number | null;
  studio?: boolean;
  pendingDocType?: string;
  appliedAddress?: {
    key: string;
    line: string;
    town: string;
    state: string;
    postcode: string;
    country: string;
    seq: number;
  } | null;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const sessionStore = useSessionStore();
const fill = provideCddFill();
const docRef = ref<InstanceType<typeof SgIncorporationPrePackageDoc> | null>(null);

const entityCd = computed(() =>
  String(
    props.entityData?.entityCd ||
      props.entityData?.icpCd ||
      props.chatFormData?.entityCd ||
      props.chatFormData?.icpCd ||
      '',
  ).trim(),
);
/** Signers 는 L_ENTITY 가 없으므로 entityCd 를 넘기지 않음 (AUTH002 방지) */
const signersCtx = provideSgPreSigners();

async function ensureCspLetterheadFilled() {
  // Setup Entity engagement.csp* 가 1순위. 비어 있을 때만 CLIENT_NM / REG_NO 폴백.
  const needName = !String(fill.values.csp_name || '').trim();
  const needUen = !String(fill.values.csp_uen || '').trim();
  if (!needName && !needUen) return;
  try {
    const customer = sessionStore.getCustomerInfo();
    if (needUen) {
      const uen = String(customer?.regNo || '').trim();
      if (uen) fill.setValue('csp_uen', uen);
    }
    if (needName) {
      const nm =
        (await fetchSessionClientNm(String(customer?.code || '').trim())) ||
        String(customer?.name || '').trim();
      if (nm) fill.setValue('csp_name', nm);
    }
  } catch (e) {
    console.warn('[SgIncorporationPre] CSP letterhead inject failed:', e);
  }
}

const taskTitleLocal = ref('');
const titleModel = computed({
  get: () => taskTitleLocal.value || props.taskTitle || '',
  set: (v: string) => {
    taskTitleLocal.value = v;
  },
});
const effectiveTitle = computed(() => titleModel.value.trim());

const studio = computed(() => !!props.studio);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => {
  presetProgress.busy = v;
  presetProgress.message = v ? progress.value || 'Creating task…' : '';
});
watch(progress, (m) => {
  if (creating.value) presetProgress.message = m;
});

const editMode = computed(() => !!props.editTaskId);
const taskConfigSteps = ref<string[]>([]);
const lastAutofillIcp = ref('');
const autofilledKeys = ref<Set<string>>(new Set());
const pendingLocalRestore = ref<{
  form45Ids: number[];
  form45bIds: number[];
  corpCtrlIds: number[];
  indCtrlIds: number[];
  csProfileIds: number[];
  caf: Record<string, number[]>;
} | null>(null);

/** Setup Entity Yes/No — No 인 섹션은 Pre 문서·Stage·Create Task 생성 제외 */
const sectionIncludes = ref<SgIncPreIncludes>({
  directors: true,
  secretary: true,
  shareholders: true,
  controllers: true,
  engagement: true,
});

/** CDD 에서 인물 추가 시 Stage 문서 포함 플래그 자동 ON */
function onSyncIncludes(partial: Partial<SgIncPreIncludes>) {
  sectionIncludes.value = {
    ...sectionIncludes.value,
    ...Object.fromEntries(
      Object.entries(partial).filter(([, v]) => v === true),
    ),
  } as SgIncPreIncludes;
}

function emitProgress() {
  const total = fill.totalFields.value || 1;
  const filled = fill.filledCount.value;
  const pct = Math.min(100, Math.round((filled / total) * 100));
  if (pct < 100 && typeof console !== 'undefined') {
    const empty = fill.keys.value.filter((k) => !isProgressFilled(k, fill.values[k], fill.values));
    if (empty.length && empty.length <= 25) {
      console.debug('[SgIncorporationPre] progress empty keys', empty);
    }
  }
  emit('progress-change', pct);
}

watch([() => fill.filledCount.value, () => fill.totalFields.value], () => emitProgress());

fill.setActivateHandler((key: string) => {
  const bare = bareKey(key);
  const t = typeOf(key);
  if (t === 'address') {
    emit('request-address-input', key);
    return;
  }
  if (t === 'ssic') {
    emit('request-person-lookup', {
      fieldGroup: 'sg-incorporation-pre',
      key: bare,
      lookupType: 'ssic',
      scopedKey: key,
    });
    return;
  }
  if (t === 'entity') {
    // 회사명 — incorporation 링크 또는 Contact(법인) 검색
    if (
      bare === 'company_name' ||
      bare === 'entity_name' ||
      bare === 'proposed_company_name'
    ) {
      emit('request-person-lookup', {
        fieldGroup: 'sg-incorporation-pre',
        key: bare,
        lookupType: 'incorporation',
        scopedKey: key,
      });
      return;
    }
    // CSP / 법인 Controllers / Corp SH — Global Contact (법인) 검색
    emit('request-person-lookup', {
      fieldGroup: 'sg-incorporation-pre',
      key: bare,
      defaultRole: 'CONTACT',
      lookupType: 'officer',
      scopedKey: key,
      // Pre 는 L_ENTITY 가 없음 — Contact 는 client-wide
      entityCd: '',
    });
    return;
  }
  if (t === 'person') {
    // 설립 전 단계 — officer 목록 대신 Global Contact 로 조회·자동바인딩
    emit('request-person-lookup', {
      fieldGroup: 'sg-incorporation-pre',
      key: bare,
      defaultRole: 'CONTACT',
      lookupType: 'officer',
      scopedKey: key,
      entityCd: '',
    });
  }
});

fill.setAddressHandler((key: string) => {
  emit('request-address-input', key);
});

watch(
  () => props.appliedAddress,
  (a) => {
    if (!a?.key) return;
    const line = [a.line, [a.postcode, a.town].filter(Boolean).join(' '), a.state, a.country]
      .map((s) => String(s || '').trim())
      .filter(Boolean)
      .join(', ');
    if (line) fill.setValue(a.key, line);
  },
);

watch(
  () => props.selectedPerson,
  async (p) => {
    if (!p) return;
    let person: Record<string, any> = { ...(p as any) };
    // bridge 는 MY registeredAddress 위주라 SG L_GLOBAL_CONTACT 주소 컬럼이 빠질 수 있음 → getContactById 로 보강
    const contactCd = String(person.contactCd || person.CONTACT_CD || '').trim();
    if (contactCd) {
      try {
        const raw: any = await contactsApi.getContactById(contactCd);
        const c: any = raw?.contact || raw?.data?.contact || raw?.data || raw || {};
        person = {
          ...c,
          ...person,
          // UPPER_CASE 컬럼이 bridge 필드에 덮이지 않도록 주소 키는 contact 원본 유지
          BLOCK_NO: c.BLOCK_NO ?? c.blockNo ?? person.BLOCK_NO,
          STREET_NAME: c.STREET_NAME ?? c.streetName ?? person.STREET_NAME,
          BUILDING_NAME: c.BUILDING_NAME ?? c.buildingName ?? person.BUILDING_NAME,
          ADDR_LEVEL: c.ADDR_LEVEL ?? c.addrLevel ?? person.ADDR_LEVEL,
          ADDR_UNIT: c.ADDR_UNIT ?? c.addrUnit ?? person.ADDR_UNIT,
          POSTAL_CODE: c.POSTAL_CODE ?? c.postalCode ?? person.POSTAL_CODE,
          FOREIGN_ADDRESS_LINE_1:
            c.FOREIGN_ADDRESS_LINE_1 ?? c.foreignAddressLine1 ?? person.FOREIGN_ADDRESS_LINE_1,
          FOREIGN_ADDRESS_LINE_2:
            c.FOREIGN_ADDRESS_LINE_2 ?? c.foreignAddressLine2 ?? person.FOREIGN_ADDRESS_LINE_2,
          ALT_BLOCK_NO: c.ALT_BLOCK_NO ?? c.altBlockNo ?? person.ALT_BLOCK_NO,
          ALT_STREET_NAME: c.ALT_STREET_NAME ?? c.altStreetName ?? person.ALT_STREET_NAME,
          ALT_BUILDING_NAME: c.ALT_BUILDING_NAME ?? c.altBuildingName ?? person.ALT_BUILDING_NAME,
          ALT_ADDR_LEVEL: c.ALT_ADDR_LEVEL ?? c.altAddrLevel ?? person.ALT_ADDR_LEVEL,
          ALT_ADDR_UNIT: c.ALT_ADDR_UNIT ?? c.altAddrUnit ?? person.ALT_ADDR_UNIT,
          ALT_POSTAL_CODE: c.ALT_POSTAL_CODE ?? c.altPostalCode ?? person.ALT_POSTAL_CODE,
          ALT_FOREIGN_ADDRESS_LINE_1:
            c.ALT_FOREIGN_ADDRESS_LINE_1 ??
            c.altForeignAddressLine1 ??
            person.ALT_FOREIGN_ADDRESS_LINE_1,
          ALT_FOREIGN_ADDRESS_LINE_2:
            c.ALT_FOREIGN_ADDRESS_LINE_2 ??
            c.altForeignAddressLine2 ??
            person.ALT_FOREIGN_ADDRESS_LINE_2,
          primaryAddressParts: c.primaryAddressParts ?? person.primaryAddressParts,
          alternateAddressParts: c.alternateAddressParts ?? person.alternateAddressParts,
          NATIONALITY_CD:
            c.NATIONALITY_CD ?? c.nationalityCd ?? person.NATIONALITY_CD ?? person.nationalityCd,
          nationality:
            c.NATIONALITY_CD ??
            c.nationalityCd ??
            c.nationality ??
            person.NATIONALITY_CD ??
            person.nationality,
          nationalityName:
            c.nationalityName ??
            c.nationalityText ??
            c.NATIONALITY_NAME ??
            person.nationalityName ??
            person.nationalityText,
          alternateAddress:
            c.alternateAddress ?? c.serviceAddress ?? person.alternateAddress ?? person.serviceAddress,
        };
      } catch (err) {
        console.warn('[SgIncorporationPre] contact address enrich failed:', err);
      }
    }
    await fill.applyPersonToActive(person);
    emitProgress();
  },
);

/** 챗봇 SSIC 선택 → 문서 필드 반영 */
watch(
  () => props.chatFormData,
  (fd) => {
    if (!fd) return;
    const target = String(fd._ssicTargetKey || '').trim();
    if (!target) return;
    const display = String(fd._ssicDisplay || fd[target] || '').trim();
    if (!display) return;
    if (fill.values[target] !== display) {
      fill.setValue(target, display);
      emitProgress();
    }
  },
  { deep: true },
);

function flushLocalRestore() {
  const pending = pendingLocalRestore.value;
  if (!pending || !docRef.value?.restoreLocalState) return;
  docRef.value.restoreLocalState(pending);
}

function resetAutofilledValues() {
  for (const k of autofilledKeys.value) {
    fill.setValue(k, '');
  }
  // 이전 엔티티 다건 키(ubo2_* 등)가 남아 행/ % 를 오염시키지 않도록 잔여 인덱스·스코프 키도 비움
  for (const k of Object.keys(fill.values)) {
    if (
      /^(dir|sh|ubo|pep|md|ceo|pa|ot|sec|ictrl|cctrl)\d+_/.test(k) ||
      /^(f45_|f45b_|ctrlc_|ctrli_|cs_)\d+\//.test(k) ||
      /^d\d+_/.test(k) ||
      /^s\d+_/.test(k)
    ) {
      fill.setValue(k, '');
    }
  }
  autofilledKeys.value = new Set();
  lastAutofillIcp.value = '';
  sectionIncludes.value = {
    directors: true,
    secretary: true,
    shareholders: true,
    controllers: true,
    engagement: true,
  };
  pendingLocalRestore.value = {
    form45Ids: [1],
    form45bIds: [1],
    corpCtrlIds: [1],
    indCtrlIds: [1],
    csProfileIds: [1],
    caf: {
      directors: [1],
      shareholders: [1],
      ubos: [1],
      peps: [1],
      mds: [1],
      ceos: [1],
      partners: [1],
      others: [1],
    },
  };
  flushLocalRestore();
  pendingLocalRestore.value = null;
}

async function autofillFromIncorporation(icpCd: string) {
  const cd = String(icpCd || '').trim();
  if (!cd || cd === lastAutofillIcp.value) return;
  // 엔티티 전환 시 이전 autofill·다건 행을 먼저 리셋 (빈 UBO 2 잔존 / 97% 고착 방지)
  if (autofilledKeys.value.size || lastAutofillIcp.value) {
    const prevKeys = autofilledKeys.value;
    for (const k of prevKeys) fill.setValue(k, '');
    for (const k of Object.keys(fill.values)) {
      if (
        /^(dir|sh|ubo|pep|md|ceo|pa|ot|sec|ictrl|cctrl)\d+_/.test(k) ||
        /^(f45_|f45b_|ctrlc_|ctrli_|cs_)\d+\//.test(k)
      ) {
        fill.setValue(k, '');
      }
    }
    autofilledKeys.value = new Set();
  }
  lastAutofillIcp.value = cd;
  try {
    const res = await fetch(`/api/aiDocuments/incorporation/entity-data/${cd}`, {
      credentials: 'include',
    });
    const json = await res.json();
    if (!json?.success) return;
    const incData = json.data?.incorporationData || {};
    if (!incData.companyName && json.data?.companyName) {
      incData.companyName = json.data.companyName;
    }
    const mapped = mapSgIncorporationToPreValues(incData);
    sectionIncludes.value = { ...mapped.includes };
    const written = new Set<string>();
    for (const [k, v] of Object.entries(mapped.values)) {
      if (v) {
        fill.setValue(k, v);
        written.add(k);
      }
    }
    autofilledKeys.value = written;
    pendingLocalRestore.value = {
      form45Ids: mapped.form45Ids,
      form45bIds: mapped.form45bIds,
      corpCtrlIds: mapped.corpCtrlIds,
      indCtrlIds: mapped.indCtrlIds,
      csProfileIds: mapped.csProfileIds,
      caf: mapped.caf,
    };
    await nextTick();
    flushLocalRestore();
    await ensureCspLetterheadFilled();
    signersCtx.seedFromValuesIfEmpty(fill.exportPayload());
    await nextTick();
    emitProgress();
  } catch (err) {
    console.warn('[SgIncorporationPre] autofill failed:', err);
    lastAutofillIcp.value = '';
  }
}

watch(
  entityCd,
  (cd, prev) => {
    if (cd) {
      // Change entity: 이전 icp 와 다르면 강제 리셋 후 autofill
      if (prev && prev !== cd) {
        lastAutofillIcp.value = '';
      }
      void autofillFromIncorporation(cd);
    } else if (prev) {
      resetAutofilledValues();
    }
  },
  { immediate: true },
);

/** Autofill 이 PackageDoc mount 전에 끝나면 N부 복원을 다시 적용 */
watch(docRef, (el) => {
  if (el) void nextTick(() => flushLocalRestore());
});

onMounted(async () => {
  try {
    await loadCmBaseCodeOptions('B04');
    // Autofill / 이전 세션에 남은 B04 코드 → 표시명
    for (const [k, v] of Object.entries(fill.values)) {
      if (!/(nationality|_country|country_of_incorporation)/i.test(k)) continue;
      const raw = String(v || '').trim();
      if (!/^B\d{5}$/i.test(raw)) continue;
      const label = nationalityLabelForOutput(raw);
      if (label && label !== raw) fill.setValue(k, label);
    }
  } catch {
    /* ignore */
  }
  try {
    const cfg = await getTaskConfigByCode(String(props.selection.task.TASK_CODE || '').trim());
    const steps = cfg?.processSteps;
    taskConfigSteps.value = Array.isArray(steps) ? steps : [];
  } catch {
    /* ignore */
  }
  await ensureCspLetterheadFilled();
});

function gatherState() {
  return {
    values: { ...fill.exportPayload() },
    local: docRef.value?.gatherLocalState?.() || null,
    includes: { ...sectionIncludes.value },
    signers: signersCtx.exportAll(),
    entityCd: entityCd.value,
    taskTitle: effectiveTitle.value,
  };
}

function restoreState(s: any) {
  if (!s) return;
  if (s.values && typeof s.values === 'object') {
    for (const [k, v] of Object.entries(s.values)) {
      if (v != null) fill.setValue(k, String(v));
    }
  }
  if (s.includes && typeof s.includes === 'object') {
    sectionIncludes.value = {
      directors: s.includes.directors !== false,
      secretary: s.includes.secretary !== false,
      shareholders: s.includes.shareholders !== false,
      controllers: s.includes.controllers !== false,
      engagement: s.includes.engagement !== false,
    };
  }
  if (s.signers) signersCtx.importAll(s.signers);
  if (s.local) {
    pendingLocalRestore.value = s.local;
    flushLocalRestore();
  }
}

const TASK_CODE = 'B32109';

async function createTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) {
    progress.value = 'Please enter a Task Title.';
    return;
  }
  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const taskTypeCd = String(props.selection.task.TASK_CODE || TASK_CODE).trim() || TASK_CODE;
    await ensureCspLetterheadFilled();
    const valuesSnap = fill.exportPayload();
    signersCtx.seedFromValuesIfEmpty(valuesSnap);
    const baseFormRaw: Record<string, any> = { ...valuesSnap };
    for (const [k, v] of Object.entries(valuesSnap)) {
      if (k.includes('/') && v) {
        const bare = bareKey(k);
        if (!baseFormRaw[bare]) baseFormRaw[bare] = v;
      }
    }
    // CSP 키가 autofill 직후 export 에 빠져 있으면 한 번 더 반영
    if (!String(baseFormRaw.csp_name || '').trim() && fill.values.csp_name) {
      baseFormRaw.csp_name = fill.values.csp_name;
    }
    const signersMap = signersCtx.exportAll();
    // ACRA Risk Signature — Signers → Word 텍스트 변수
    const recSig = signersMap[SG_PRE_SIGNER_KEYS.riskRecommend]?.[0]?.name;
    const appSig = signersMap[SG_PRE_SIGNER_KEYS.riskApprove]?.[0]?.name;
    if (recSig) baseFormRaw.recommending_officer_signature = String(recSig).trim();
    if (appSig) baseFormRaw.approving_officer_signature = String(appSig).trim();
    const baseForm = normalizeSgPreFormDataForWord(baseFormRaw);
    const fallbackSigners = buildSgPreSignersFromValues(valuesSnap);

    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };
    const officerData = {
      entityCd: entityCd.value || null,
      icpCd: entityCd.value || null,
      presetForm: gatherState(),
      docType: 'SG_INCORPORATION_PRE',
      formData: baseForm,
      signers: signersMap,
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) {
        progress.value = 'Error: invalid edit task id';
        creating.value = false;
        return;
      }
      await fetch(`/api/startTask/tasks/${taskId}/data`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskData: { formData: officerData } }),
      });
      await fetch(`/api/startTask/tasks/${taskId}/title`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskTitle: effectiveTitle.value }),
      });
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null,
          taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME || 'Incorporation (Pre)',
          taskTitle: effectiveTitle.value,
          taskFlow,
          officerData,
        }),
      }).then((r) => r.json());
      taskId = Number(taskRes?.data?.taskId || taskRes?.taskId || 0);
      if (!taskId) {
        progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`;
        creating.value = false;
        return;
      }
    }

    progress.value = 'Preparing documents…';
    const docs = props.selection.documents || [];
    const local = docRef.value?.gatherLocalState?.() || pendingLocalRestore.value || {
      form45Ids: [1],
      form45bIds: [1],
      corpCtrlIds: [1],
      indCtrlIds: [1],
      csProfileIds: [1],
    };
    const companyLabel = String(baseForm.company_name || baseForm.entity_name || 'Company');

    type DocSave = {
      templateId: number;
      label: string;
      personName: string;
      entityCd?: string;
      formData: Record<string, any>;
      signers?: any[];
    };
    const docsToSave: DocSave[] = [];

    type SgPreKind =
      | 'form45'
      | 'form45b'
      | 'notice_corp'
      | 'notice_ind'
      | 'cs_profile'
      | 'engagement'
      | 'constitution'
      | 'client'
      | 'risk'
      | 'other';
    function classifySgPreDoc(name: string): SgPreKind {
      const n = String(name || '').toLowerCase();
      if (/form\s*45\s*b|form45b/.test(n)) return 'form45b';
      if (/form\s*45|form45/.test(n)) return 'form45';
      if (/notice/.test(n) && /(corporate|corp)/.test(n)) return 'notice_corp';
      if (/notice/.test(n) && /(individual|ind)/.test(n)) return 'notice_ind';
      if (/notice/.test(n) && /controller/.test(n)) return 'notice_ind';
      if (/corporate\s*shareholder|shareholder\s*profile/.test(n)) return 'cs_profile';
      if (/engagement/.test(n)) return 'engagement';
      if (/constitution/.test(n)) return 'constitution';
      if (/client\s*acceptance/.test(n)) return 'client';
      if (/risk\s*assessment|acra\s*risk/.test(n)) return 'risk';
      return 'other';
    }
    /** Form 45 (공통 최신본) 이사 표 + 증인 — 사본(f45_{id}/)마다 독립인 키. */
    const F45_PERSON_KEYS = [
      'director_name', 'director_address', 'director_alternate_address', 'director_identification_no',
      'director_nationality', 'director_date_of_birth', 'director_email', 'director_mobile_no',
      'witness_name', 'witness_address',
    ];
    function formDataForScope(scopePrefix: string) {
      const out: Record<string, any> = {};
      for (const [k, v] of Object.entries(baseForm)) {
        if (!String(k).includes('/')) out[k] = v;
      }
      for (const [k, v] of Object.entries(baseForm)) {
        if (!String(k).startsWith(scopePrefix)) continue;
        const bare = String(k).slice(scopePrefix.length);
        if (bare) out[bare] = v;
        out[k] = v;
      }
      return normalizeSgPreFormDataForWord(out);
    }
    function signersForKind(kind: SgPreKind): any[] | undefined {
      const key =
        kind === 'engagement'
          ? SG_PRE_SIGNER_KEYS.engagement
          : kind === 'constitution'
            ? SG_PRE_SIGNER_KEYS.constitution
            : kind === 'client'
              ? SG_PRE_SIGNER_KEYS.client
              : '';
      if (!key) return undefined;
      const list = signersMap[key];
      if (list?.length) return list;
      return fallbackSigners.length ? fallbackSigners : undefined;
    }

    /** 인물 스코프 문서(Form45/45B/Notice/CS Profile) 용 signer 1건 —
     *  T-Sign 화면에서 신청자 이름/이메일 자동바인딩되도록 각 문서에 태워보낸다.
     *  scope 안 name/email 필드는 kind 마다 다르므로 후보 키 목록에서 첫 번째로 채운다. */
    let personSignerSeq = 0;
    function personSigner(
      fd: Record<string, any>,
      nameKeys: string[],
      emailKeys: string[],
      role: string,
      title: string,
    ): any[] | undefined {
      const name = nameKeys.map((k) => String(fd[k] || '').trim()).find(Boolean) || '';
      const email = emailKeys.map((k) => String(fd[k] || '').trim()).find(Boolean) || '';
      if (!name && !email) return undefined;
      personSignerSeq += 1;
      return [{
        id: `sg-pre-person-${personSignerSeq}`,
        name, email, title, role,
        isManual: true, showTitle: !!title, _signerLayout: 'box',
      }];
    }
    function pushOnce(
      d: any,
      formData: Record<string, any>,
      personName: string,
      labelSuffix = '',
      signers?: any[],
    ) {
      const tid = Number(d?.tmpId || d?.templateId || 0);
      if (!tid) return;
      const baseLabel = String(d?.templateName || d?.docCode || 'Document').replace(/\.docx?$/i, '');
      docsToSave.push({
        templateId: tid,
        label: labelSuffix ? `${baseLabel} — ${labelSuffix}` : baseLabel,
        personName,
        // Pre 단계 icpCd 는 L_ENTITY 가 아님 — prepare-documents 에 entityCd 로 넣지 않음
        formData: normalizeSgPreFormDataForWord(formData),
        signers: signers?.length ? signers : undefined,
      });
    }

    const incl = sectionIncludes.value;
    for (const d of docs) {
      const kind = classifySgPreDoc((d as any).templateName || (d as any).docCode || '');
      if (kind === 'form45') {
        if (!incl.directors) continue;
        const ids = local.form45Ids?.length ? local.form45Ids : [];
        for (const id of ids) {
          const scope = `f45_${id}/`;
          const fd = formDataForScope(scope);
          // Form 45 이사 표·증인 칸은 **이 이사 본인 값만** — 비었을 때 1번 이사/다른 사본 값이
          //   (최상위·dir1_ 접기 경로로) 채워지면 다른 사람 정보가 찍힌다.
          for (const k of F45_PERSON_KEYS) fd[k] = String(valuesSnap[`${scope}${k}`] ?? '').trim();
          const person = String(fd.director_name || `Director ${id}`);
          // Form 45 는 신규 director 본인이 서명 → 그 사람 이름/이메일을 signer 로.
          const signer = personSigner(fd,
            ['director_name', 'name'],
            ['director_email', 'email'],
            'director', 'Director');
          pushOnce(d, fd, person, person, signer);
        }
        continue;
      }
      if (kind === 'form45b') {
        if (!incl.secretary) continue;
        const ids = local.form45bIds?.length ? local.form45bIds : [];
        for (const id of ids) {
          const scope = `f45b_${id}/`;
          const fd = formDataForScope(scope);
          const person = String(fd.secretary_name || `Secretary ${id}`);
          const signer = personSigner(fd,
            ['secretary_name', 'name'],
            ['secretary_email', 'email'],
            'secretary', 'Secretary');
          pushOnce(d, fd, person, person, signer);
        }
        continue;
      }
      if (kind === 'notice_corp') {
        if (!incl.controllers) continue;
        const ids = local.corpCtrlIds?.length ? local.corpCtrlIds : [];
        for (const id of ids) {
          const scope = `ctrlc_${id}/`;
          const fd = formDataForScope(scope);
          const person = String(fd.controller_corporate_name || `Corporate Controller ${id}`);
          // Notice 386AG (Corporate) — 통보 대상 법인이 서명 (담당자 이름/이메일).
          const signer = personSigner(fd,
            ['controller_corporate_contact_person', 'controller_corporate_name', 'name'],
            ['controller_corporate_email', 'controller_email', 'email'],
            'controller', 'Controller');
          pushOnce(d, fd, person, person, signer);
        }
        continue;
      }
      if (kind === 'notice_ind') {
        if (!incl.controllers) continue;
        const ids = local.indCtrlIds?.length ? local.indCtrlIds : [];
        for (const id of ids) {
          const scope = `ctrli_${id}/`;
          const fd = formDataForScope(scope);
          const person = String(fd.controller_full_name || `Individual Controller ${id}`);
          const signer = personSigner(fd,
            ['controller_full_name', 'controller_name', 'name'],
            ['controller_email', 'email'],
            'controller', 'Controller');
          pushOnce(d, fd, person, person, signer);
        }
        continue;
      }
      if (kind === 'cs_profile') {
        if (!incl.shareholders) continue;
        const ids = local.csProfileIds?.length ? local.csProfileIds : [];
        for (const id of ids) {
          const scope = `cs_${id}/`;
          const fd = formDataForScope(scope);
          const person = String(fd.cs_company_name || `Corporate Shareholder ${id}`);
          const signer = personSigner(fd,
            ['cs_contact_person', 'cs_company_name', 'name'],
            ['cs_email', 'email'],
            'shareholder', 'Authorised Signatory');
          pushOnce(d, fd, person, person, signer);
        }
        continue;
      }
      if (kind === 'engagement' && !incl.engagement) continue;
      pushOnce(d, { ...baseForm }, companyLabel, '', signersForKind(kind));
    }

    if (docsToSave.length) {
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            taskId,
            documents: docsToSave,
            replaceExisting: editMode.value,
          }),
        }).then((r) => r.json());
        if (!saveRes?.success) {
          progress.value = `⚠ Documents: ${saveRes?.message || 'unknown'}`;
          console.warn('[SgIncorporationPre] prepare-documents failed:', saveRes);
        }
      } catch (err: any) {
        console.warn('[SgIncorporationPre] document save failed:', err);
        progress.value = `⚠ Document save failed: ${err?.message || err}`;
      }
    }

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch {
      /* ignore */
    }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          presetTaskId: props.selection.task.PRESET_TASK_ID,
          taskId,
        }),
      });
    } catch {
      /* ignore */
    }

    emit('task-created', { taskId: taskId!, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    console.error('[SgIncorporationPre] create failed', e);
    progress.value = e?.message || 'Create failed';
  } finally {
    creating.value = false;
  }
}

defineExpose({ gatherState, restoreState, createTask });
</script>

<template>
  <div class="sg-inc-pre-panel" :class="{ studio }">
    <div v-if="!studio" class="co-head">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME || 'Incorporation (Pre)' }}</div>
        <div class="co-subtitle">SG Incorporation (Pre) — live document package</div>
      </div>
      <div class="co-actions">
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-task-title')">Change title</v-btn>
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-entity')">Change entity</v-btn>
      </div>
    </div>

    <div v-if="!studio" class="co-title-row">
      <v-text-field
        v-model="titleModel"
        density="compact"
        variant="outlined"
        hide-details
        label="Task Title"
        placeholder="e.g. SG Incorporation (Pre) — NewCo Pte. Ltd."
      />
    </div>

    <div v-if="!studio && entityCd" class="co-entity">
      Linked incorporation: <strong>{{ entityData?.entityName || entityData?.companyName || entityCd }}</strong>
    </div>

    <div class="co-doc">
      <SgIncorporationPrePackageDoc
        ref="docRef"
        :includes="sectionIncludes"
        @sync-includes="onSyncIncludes"
      />
    </div>

    <div v-if="!studio" class="co-foot">
      <v-btn
        color="primary"
        class="text-none"
        :loading="creating"
        :disabled="!effectiveTitle || creating"
        @click="createTask"
      >
        {{ editMode ? 'Update task' : 'Create Task' }}
      </v-btn>
      <span v-if="progress && !creating" class="co-err">{{ progress }}</span>
    </div>
    <div v-else-if="progress && !creating" class="co-foot">
      <span class="co-err">{{ progress }}</span>
    </div>
  </div>
</template>

<style scoped>
.sg-inc-pre-panel { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
.sg-inc-pre-panel.studio .co-foot { position: sticky; bottom: 0; background: #fff; padding: 10px 0; z-index: 2; }
.co-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.co-title { font-size: 16px; font-weight: 800; color: #1a2336; }
.co-subtitle { font-size: 12px; color: #6b7a94; margin-top: 2px; }
.co-actions { display: flex; gap: 4px; flex-shrink: 0; }
.co-title-row { max-width: 520px; }
.co-entity { font-size: 12.5px; color: #42506a; }
.co-doc { flex: 1; min-height: 0; }
.co-foot { display: flex; align-items: center; gap: 12px; }
.co-err { font-size: 12px; color: #c0392b; }
</style>
